import { useState, type FormEvent } from 'react';
import { Link, useActionData, useLoaderData, useNavigation, useSubmit } from 'react-router-dom';
import { Button, FormField, Input, Textarea } from '@dma/ui';
import { ImageUploadField } from '../../media';
import { ScientificMarkdown } from './ScientificMarkdown';
import type {
  EntryImage,
  EntryReference,
  PhysicalProperties,
  ScientificEntryCategory,
  ScientificEntryStatus
} from '../domain/entities/ScientificEntry.entity';
import type { ScientificEntryDto } from '../application/dto/ScientificEntryDto';
import type { SaveScientificEntryDto } from '../application/dto/SaveScientificEntryDto';
import './AdminScientificEntryFormPage.css';

export interface AdminScientificEntryFormLoaderData {
  heading: string;
  basePath: string;
  category: ScientificEntryCategory;
  /** null in "new" mode, the existing entry in "edit" mode. */
  entry: ScientificEntryDto | null;
}

interface ActionFailure {
  ok: false;
  message: string;
}

const EMPTY_PROPERTIES: PhysicalProperties = {
  hardness: null,
  color: null,
  luster: null,
  streak: null,
  crystalSystem: null,
  cleavage: null,
  fracture: null,
  specificGravity: null
};

const PROPERTY_FIELDS: Array<{ key: keyof PhysicalProperties; label: string }> = [
  { key: 'hardness', label: 'سختی (مقیاس موس، مثلاً 5 یا 5.5-6)' },
  { key: 'color', label: 'رنگ' },
  { key: 'luster', label: 'جلا' },
  { key: 'streak', label: 'رنگ خط' },
  { key: 'crystalSystem', label: 'سیستم بلوری' },
  { key: 'cleavage', label: 'رخ' },
  { key: 'fracture', label: 'شکست' },
  { key: 'specificGravity', label: 'وزن مخصوص' }
];

function toFormState(entry: ScientificEntryDto | null, category: ScientificEntryCategory) {
  return {
    title: entry?.title ?? '',
    slug: entry?.slug ?? '',
    classification: entry?.classification ?? '',
    chemicalFormula: entry?.chemicalFormula ?? '',
    physicalProperties: entry?.physicalProperties ?? EMPTY_PROPERTIES,
    description: entry?.description ?? '',
    images: entry?.images ?? [],
    occurrenceLocationsText: (entry?.occurrenceLocations ?? []).join('\n'),
    references: entry?.references ?? [],
    status: entry?.status ?? ('draft' as ScientificEntryStatus),
    category
  };
}

/**
 * Used for both `/admin/mineralogy/new` + `/admin/mineralogy/:id/edit` (and
 * the petrology equivalents) — the route's loader decides create vs. edit by
 * whether `entry` is null, everything else about the form is identical.
 *
 * Submits as a single JSON payload via `useSubmit(..., { encType:
 * 'application/json' })` rather than a plain `<Form>` — the images and
 * references lists are dynamic, nested arrays that don't map cleanly onto
 * flat FormData field names, and this app's React Router version supports
 * `request.json()` in the route action, so there is no need for a form
 * library just to serialize two small repeatable-field lists.
 */
export function AdminScientificEntryFormPage() {
  const { heading, basePath, category, entry } = useLoaderData() as AdminScientificEntryFormLoaderData;
  const actionData = useActionData() as ActionFailure | undefined;
  const navigation = useNavigation();
  const submit = useSubmit();
  const isSubmitting = navigation.state === 'submitting';

  const [form, setForm] = useState(() => toFormState(entry, category));
  const [showDescriptionPreview, setShowDescriptionPreview] = useState(false);

  function updateField<K extends keyof ReturnType<typeof toFormState>>(
    key: K,
    value: ReturnType<typeof toFormState>[K]
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateProperty(key: keyof PhysicalProperties, value: string) {
    setForm((prev) => ({
      ...prev,
      physicalProperties: { ...prev.physicalProperties, [key]: value.trim() === '' ? null : value }
    }));
  }

  function addImage() {
    setForm((prev) => ({ ...prev, images: [...prev.images, { url: '', caption: '' }] }));
  }

  function updateImage(index: number, patch: Partial<EntryImage>) {
    setForm((prev) => ({
      ...prev,
      images: prev.images.map((image, i) => (i === index ? { ...image, ...patch } : image))
    }));
  }

  function removeImage(index: number) {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  }

  function addReference() {
    setForm((prev) => ({ ...prev, references: [...prev.references, { title: '', url: null, author: null }] }));
  }

  function updateReference(index: number, patch: Partial<EntryReference>) {
    setForm((prev) => ({
      ...prev,
      references: prev.references.map((reference, i) => (i === index ? { ...reference, ...patch } : reference))
    }));
  }

  function removeReference(index: number) {
    setForm((prev) => ({ ...prev, references: prev.references.filter((_, i) => i !== index) }));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const payload: SaveScientificEntryDto = {
      id: entry?.id ?? null,
      slug: form.slug,
      title: form.title,
      category: form.category,
      classification: form.classification,
      chemicalFormula: form.chemicalFormula.trim() === '' ? null : form.chemicalFormula,
      physicalProperties: form.physicalProperties,
      description: form.description,
      images: form.images.filter((image) => image.url.trim() !== ''),
      occurrenceLocations: form.occurrenceLocationsText
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0),
      references: form.references.filter((reference) => reference.title.trim() !== ''),
      status: form.status
    };

    submit(payload as unknown as Record<string, unknown>, { method: 'post', encType: 'application/json' });
  }

  return (
    <div className="dma-admin-scientific-form">
      <Link to={basePath} className="dma-admin-scientific-form__back">
        ← بازگشت به فهرست
      </Link>
      <h1>{heading}</h1>

      <form onSubmit={handleSubmit} noValidate>
        {actionData && actionData.ok === false ? (
          <p className="dma-admin-scientific-form__error" role="alert">
            {actionData.message}
          </p>
        ) : null}

        <section className="dma-admin-scientific-form__section">
          <h2>اطلاعات پایه</h2>

          <FormField label="عنوان" htmlFor="title" required>
            <Input
              id="title"
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              required
              disabled={isSubmitting}
            />
          </FormField>

          <FormField label="اسلاگ (فقط حروف انگلیسی کوچک و خط تیره، برای آدرس صفحه)" htmlFor="slug" required>
            <Input
              id="slug"
              value={form.slug}
              onChange={(e) => updateField('slug', e.target.value)}
              placeholder="quartz یا mica-schist"
              required
              disabled={isSubmitting}
            />
          </FormField>

          <FormField label="دسته‌بندی علمی (مثلاً سیلیکات‌ها یا آذرین)" htmlFor="classification" required>
            <Input
              id="classification"
              value={form.classification}
              onChange={(e) => updateField('classification', e.target.value)}
              required
              disabled={isSubmitting}
            />
          </FormField>

          <FormField label="فرمول شیمیایی (اختیاری)" htmlFor="chemicalFormula">
            <Input
              id="chemicalFormula"
              value={form.chemicalFormula ?? ''}
              onChange={(e) => updateField('chemicalFormula', e.target.value)}
              disabled={isSubmitting}
            />
          </FormField>

          <FormField label="توضیحات (از Markdown پشتیبانی می‌کند)" htmlFor="description">
            <Textarea
              id="description"
              rows={10}
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              disabled={isSubmitting}
            />
          </FormField>

          <div className="dma-admin-scientific-form__markdown-hint">
            <p>راهنمای Markdown — همه‌ی این‌ها در سایت به شکل واقعی نمایش داده می‌شوند:</p>
            <ul>
              <li>
                <code>## کاربردهای کلسیت</code> — تیتر
              </li>
              <li>
                <code>**متن پررنگ**</code> و <code>*متن مورب*</code> — پررنگ و مورب
              </li>
              <li>
                <code>[کوارتز چیست؟](/mineralogy/quartz)</code> — لینک به یک صفحه‌ی دیگر از همین سایت
                (بدون رفرش کامل صفحه باز می‌شود)
              </li>
              <li>
                <code>[ویکی‌پدیا](https://en.wikipedia.org/)</code> — لینک به یک سایت خارجی (در تب جدید
                باز می‌شود)
              </li>
              <li>
                <code>- مورد اول</code> در خط‌های جدا — لیست بدون‌شماره؛ با <code>1.</code> — لیست
                شماره‌دار
              </li>
              <li>یک جدول Markdown استاندارد هم پشتیبانی می‌شود.</li>
            </ul>
            <button
              type="button"
              className="dma-admin-scientific-form__preview-toggle"
              onClick={() => setShowDescriptionPreview((prev) => !prev)}
            >
              {showDescriptionPreview ? 'بستن پیش‌نمایش' : 'پیش‌نمایش'}
            </button>
          </div>

          {showDescriptionPreview ? (
            <div className="dma-admin-scientific-form__preview-box">
              {form.description.trim() ? (
                <ScientificMarkdown content={form.description} />
              ) : (
                <p className="dma-admin-scientific-form__preview-empty">چیزی برای پیش‌نمایش نوشته نشده.</p>
              )}
            </div>
          ) : null}
        </section>

        <section className="dma-admin-scientific-form__section">
          <h2>خواص فیزیکی و شیمیایی</h2>
          <div className="dma-admin-scientific-form__properties-grid">
            {PROPERTY_FIELDS.map((field) => (
              <FormField key={field.key} label={field.label} htmlFor={`property-${field.key}`}>
                <Input
                  id={`property-${field.key}`}
                  value={form.physicalProperties[field.key] ?? ''}
                  onChange={(e) => updateProperty(field.key, e.target.value)}
                  disabled={isSubmitting}
                />
              </FormField>
            ))}
          </div>
        </section>

        <section className="dma-admin-scientific-form__section">
          <h2>تصاویر</h2>
          {form.images.map((image, index) => (
            <div className="dma-admin-scientific-form__image-row" key={index}>
              <ImageUploadField
                label={`تصویر ${index + 1}`}
                fieldId={`entry-image-${index}`}
                value={image.url}
                onChange={(url) => updateImage(index, { url })}
                folder="scientific-content"
                disabled={isSubmitting}
              />
              <div className="dma-admin-scientific-form__image-row-caption">
                <Input
                  placeholder="توضیح تصویر (اختیاری)"
                  value={image.caption}
                  onChange={(e) => updateImage(index, { caption: e.target.value })}
                  disabled={isSubmitting}
                />
                <button type="button" onClick={() => removeImage(index)} disabled={isSubmitting}>
                  حذف این تصویر
                </button>
              </div>
            </div>
          ))}
          <button type="button" className="dma-admin-scientific-form__add-row" onClick={addImage} disabled={isSubmitting}>
            + افزودن تصویر
          </button>
        </section>

        <section className="dma-admin-scientific-form__section">
          <h2>محل‌های یافت شدن</h2>
          <FormField label="هر مورد را در یک خط جداگانه بنویسید" htmlFor="occurrenceLocations">
            <Textarea
              id="occurrenceLocations"
              rows={4}
              value={form.occurrenceLocationsText}
              onChange={(e) => updateField('occurrenceLocationsText', e.target.value)}
              disabled={isSubmitting}
            />
          </FormField>
        </section>

        <section className="dma-admin-scientific-form__section">
          <h2>منابع</h2>
          {form.references.map((reference, index) => (
            <div className="dma-admin-scientific-form__repeatable-row dma-admin-scientific-form__repeatable-row--reference" key={index}>
              <Input
                placeholder="عنوان منبع"
                value={reference.title}
                onChange={(e) => updateReference(index, { title: e.target.value })}
                disabled={isSubmitting}
              />
              <Input
                placeholder="نویسنده (اختیاری)"
                value={reference.author ?? ''}
                onChange={(e) => updateReference(index, { author: e.target.value || null })}
                disabled={isSubmitting}
              />
              <Input
                placeholder="لینک (اختیاری)"
                value={reference.url ?? ''}
                onChange={(e) => updateReference(index, { url: e.target.value || null })}
                disabled={isSubmitting}
              />
              <button type="button" onClick={() => removeReference(index)} disabled={isSubmitting}>
                حذف
              </button>
            </div>
          ))}
          <button
            type="button"
            className="dma-admin-scientific-form__add-row"
            onClick={addReference}
            disabled={isSubmitting}
          >
            + افزودن منبع
          </button>
        </section>

        <section className="dma-admin-scientific-form__section">
          <h2>وضعیت انتشار</h2>
          <div className="dma-admin-scientific-form__status-options">
            <label>
              <input
                type="radio"
                name="status"
                value="draft"
                checked={form.status === 'draft'}
                onChange={() => updateField('status', 'draft')}
                disabled={isSubmitting}
              />
              پیش‌نویس (فقط در پنل مدیریت قابل مشاهده است)
            </label>
            <label>
              <input
                type="radio"
                name="status"
                value="published"
                checked={form.status === 'published'}
                onChange={() => updateField('status', 'published')}
                disabled={isSubmitting}
              />
              منتشرشده (روی سایت عمومی نمایش داده می‌شود)
            </label>
          </div>
        </section>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'در حال ذخیره...' : 'ذخیره'}
        </Button>
      </form>
    </div>
  );
}
